package com.swetdot.swetgram.ui.premium

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.ui.design.Motion
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetEasing
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIconButton
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.swetTween
import com.swetdot.swetgram.ui.design.swetPressScale

/** Paywall-only palette. Keeping it local prevents the redesign from changing unrelated screens. */
internal object PremiumPalette {
    val Background = Color(0xFF050505)
    val CardTop = Color(0xFF1B1B1B)
    val CardBottom = Color(0xFF111111)
    val IconSurface = Color(0xFF202020)
    val Primary = Color(0xFFF7F7F7)
    val Secondary = Color(0xFFA9A9A9)
    val Tertiary = Color(0xFF8F8F8F)
    val Inverse = Color(0xFF080808)
    val Border = Color.White.copy(alpha = 0.08f)
    val PlanBorder = Color.White.copy(alpha = 0.11f)
    val SelectedBorder = Color(0xFFF2F2F2)
}

internal val PremiumHorizontalMargin = 16.dp

@Composable
internal fun PremiumTopBar(title: String, backLabel: String, onBack: () -> Unit) {
    Column(Modifier.fillMaxWidth().background(PremiumPalette.Background)) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp)
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SwetIconButton(contentDescription = backLabel, onClick = onBack) { tint ->
                SwetIcon(
                    icon = SwetIcons.ChevronLeft,
                    tint = tint,
                    contentDescription = null,
                    size = 24.dp,
                )
            }
            Spacer(Modifier.width(8.dp))
            Text(
                text = title,
                color = PremiumPalette.Primary,
                style = TextStyle(
                    fontSize = 20.sp,
                    lineHeight = 25.sp,
                    fontWeight = FontWeight.SemiBold,
                ),
                maxLines = 2,
            )
        }
        Box(Modifier.fillMaxWidth().height(1.dp).background(Color.White.copy(alpha = 0.05f)))
    }
}

@Composable
internal fun SubscriptionHero(
    headline: String,
    description: String,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(42.dp)
                .drawBehind {
                    val center = Offset(size.width / 2f, 20.dp.toPx())
                    drawCircle(
                        brush = Brush.radialGradient(
                            colors = listOf(
                                Color.White.copy(alpha = 0.095f),
                                Color.White.copy(alpha = 0.025f),
                                Color.Transparent,
                            ),
                            center = center,
                            radius = 54.dp.toPx(),
                        ),
                        radius = 54.dp.toPx(),
                        center = center,
                    )
                    val arcHeight = 104.dp.toPx()
                    drawArc(
                        color = Color.White.copy(alpha = 0.105f),
                        startAngle = 0f,
                        sweepAngle = 180f,
                        useCenter = false,
                        topLeft = Offset(-size.width * 0.14f, -arcHeight * 0.45f),
                        size = Size(size.width * 1.28f, arcHeight),
                        style = Stroke(width = 1.dp.toPx(), pathEffect = PathEffect.cornerPathEffect(1f)),
                    )
                },
            contentAlignment = Alignment.Center,
        ) {
            SwetIcon(
                icon = SwetIcons.Crystal,
                tint = Color.White,
                contentDescription = null,
                size = 34.dp,
            )
        }
        Text(
            text = headline,
            color = PremiumPalette.Primary,
            style = TextStyle(
                fontSize = 19.sp,
                lineHeight = 23.sp,
                fontWeight = FontWeight.Bold,
            ),
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(4.dp))
        Text(
            text = description,
            color = PremiumPalette.Secondary,
            style = TextStyle(fontSize = 11.sp, lineHeight = 14.sp),
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth(0.92f),
        )
    }
}

@Composable
internal fun SubscriptionPlanCard(
    title: String,
    price: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    saving: String? = null,
) {
    val interaction = remember { MutableInteractionSource() }
    val borderColor by animateColorAsState(
        targetValue = if (selected) PremiumPalette.SelectedBorder else PremiumPalette.PlanBorder,
        animationSpec = swetTween(Motion.fast, SwetEasing.enter),
        label = "premiumPlanBorder",
    )
    val borderWidth by animateDpAsState(
        targetValue = if (selected) 2.dp else 1.dp,
        animationSpec = swetTween(Motion.fast, SwetEasing.enter),
        label = "premiumPlanBorderWidth",
    )
    val shape = RoundedCornerShape(16.dp)

    BoxWithConstraints(
        modifier = modifier
            .heightIn(min = 66.dp)
            .swetPressScale(interaction)
            .clip(shape)
            .background(Brush.verticalGradient(listOf(PremiumPalette.CardTop, PremiumPalette.CardBottom)))
            .border(borderWidth, borderColor, shape)
            .clickable(
                interactionSource = interaction,
                indication = null,
                role = Role.RadioButton,
                onClick = onClick,
            )
            .semantics { this.selected = selected }
            .padding(horizontal = 12.dp, vertical = 7.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        val compact = maxWidth < 115.dp
        if (selected) {
            SwetIcon(
                icon = SwetIcons.Check,
                tint = PremiumPalette.Primary,
                contentDescription = null,
                size = 16.dp,
                modifier = Modifier.align(Alignment.TopEnd),
            )
        }
        Column(verticalArrangement = Arrangement.Center) {
            Text(
                text = title,
                color = if (selected) PremiumPalette.Primary else Color(0xFFE2E2E2),
                style = TextStyle(
                    fontSize = if (compact) 12.sp else 14.sp,
                    lineHeight = if (compact) 15.sp else 17.sp,
                    fontWeight = FontWeight.SemiBold,
                ),
            )
            Spacer(Modifier.height(2.dp))
            Text(
                text = price,
                color = if (selected) Color.White else Color(0xFFBDBDBD),
                style = TextStyle(
                    fontSize = if (compact) 17.sp else 20.sp,
                    lineHeight = if (compact) 20.sp else 23.sp,
                    fontWeight = FontWeight.Bold,
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            if (saving != null) {
                Spacer(Modifier.height(2.dp))
                Text(
                    text = saving,
                    color = PremiumPalette.Primary,
                    style = TextStyle(
                        fontSize = if (compact) 11.sp else 12.sp,
                        lineHeight = if (compact) 14.sp else 15.sp,
                        fontWeight = FontWeight.SemiBold,
                    ),
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
internal fun PremiumBenefitCard(
    icon: ImageVector,
    title: String,
    description: String,
    modifier: Modifier = Modifier,
) {
    val shape = RoundedCornerShape(14.dp)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 56.dp)
            .clip(shape)
            .background(Brush.linearGradient(listOf(PremiumPalette.CardTop, PremiumPalette.CardBottom)))
            .border(1.dp, PremiumPalette.Border, shape)
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(28.dp)
                .clip(CircleShape)
                .background(PremiumPalette.IconSurface),
            contentAlignment = Alignment.Center,
        ) {
            SwetIcon(icon = icon, tint = Color.White, contentDescription = null, size = 16.dp)
        }
        Spacer(Modifier.width(9.dp))
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.Center) {
            Text(
                text = title,
                color = PremiumPalette.Primary,
                style = TextStyle(fontSize = 13.sp, lineHeight = 16.sp, fontWeight = FontWeight.SemiBold),
                maxLines = 2,
            )
            Spacer(Modifier.height(1.dp))
            Text(
                text = description,
                color = PremiumPalette.Secondary,
                style = TextStyle(fontSize = 10.sp, lineHeight = 13.sp),
            )
        }
    }
}

@Composable
internal fun PremiumSurfaceCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    val shape = RoundedCornerShape(18.dp)
    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(shape)
            .background(Brush.linearGradient(listOf(PremiumPalette.CardTop, PremiumPalette.CardBottom)))
            .border(1.dp, PremiumPalette.Border, shape)
            .padding(14.dp),
    ) {
        content()
    }
}

@Composable
internal fun SubscriptionCta(
    text: String,
    enabled: Boolean,
    loading: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val interaction = remember { MutableInteractionSource() }
    val pressed by interaction.collectIsPressedAsState()
    val container by animateColorAsState(
        targetValue = if (pressed) Color(0xFFE9E9E9) else Color.White,
        animationSpec = swetTween(Motion.instant, SwetEasing.enter),
        label = "premiumCtaFill",
    )
    Button(
        onClick = onClick,
        enabled = enabled && !loading,
        interactionSource = interaction,
        shape = RoundedCornerShape(16.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = container,
            contentColor = PremiumPalette.Inverse,
            disabledContainerColor = Color(0xFF282828),
            disabledContentColor = PremiumPalette.Tertiary,
        ),
        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 16.dp),
        modifier = modifier.fillMaxWidth().height(46.dp),
    ) {
        Text(
            text = text,
            style = TextStyle(fontSize = 14.sp, lineHeight = 18.sp, fontWeight = FontWeight.Bold),
            textAlign = TextAlign.Center,
        )
    }
}
